import {
  type AttributePayload,
  type RoleCondition,
} from '@bizzdesign/sdk-knowledgegraph';
import {
  attributeTypeToOperatorTypeMap,
  type OperatorType,
  type SearchScope,
} from '@bizzdesign/sdk-search';
import {
  assertIsUUID,
  generateUUID,
  isUUID,
  isUUIDs,
  type UUID,
} from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  defaultAttributeFilterValue,
  type SearchParameters,
} from '@bizzdesign/data-access-search';
import {
  BDCoreMetaModelDefinition,
  type ObjectType,
} from '@bizzdesign/sdk-knowledgegraph/bdcore';
import {
  type AttributeCategoryDefinitionDto,
  type AttributeFilterValue,
  type RelationCondition,
  RelationConditions,
  type RelationFilterValue,
  type RelationTarget,
  RelationTargets,
  RoleNames,
  type SystemAttributesFilter,
  type SystemAttributesUser,
  defaultSearchScopes,
} from '@bizzdesign/shared/models';
import { isEmpty, isEqual } from 'lodash-es';
import type { Params } from '@angular/router';
import { BDCore } from '@bizzdesign/sdk-knowledgegraph';

type AttributeDefinitionMapValue = {
  categoryId: UUID;
  categoryName: string;
  categoryElementType: string;
  attributeDefinition: AttributeCategoryDefinitionDto['attributeDefinitions'][number];
};

type AttributeDefinitionsMap = Record<UUID, AttributeDefinitionMapValue>;

type ParsedAttributePayloadAndObjectId = {
  attribute?: AttributePayload<'BDCore'>;
  objectId?: UUID;
};

export type AttributeFilterValueAndObjectId = {
  filterValue?: AttributeFilterValue;
  objectId?: UUID;
};

type AttributeFilterPrimitiveValue = string | number | boolean;

type AttributeFilterUrlTuple = [string, string, AttributeFilterPrimitiveValue];

export type RelationFilterValueAndObjectId = {
  filterValue?: RelationFilterValue;
  objectId?: UUID;
};

type RelationFilterUrlTuple = [
  string,
  string | null,
  string,
  string[] | null,
  string[] | null,
];

const MAX_RAW_LENGTH_PER_PARAM = 5000;

// Helpers
function isStringArray(decoded: unknown): decoded is string[] {
  return (
    Array.isArray(decoded) &&
    decoded.every((entry: unknown) => typeof entry === 'string')
  );
}

function isStringArrayOrNull(decoded: unknown): decoded is string[] | null {
  return (
    decoded === null ||
    (Array.isArray(decoded) &&
      decoded.every((entry: unknown) => typeof entry === 'string'))
  );
}

function isStringOrNull(decoded: unknown): decoded is string | null {
  return decoded === null || typeof decoded === 'string';
}

function isAttributeFilterUrlTuple(
  decoded: unknown,
): decoded is AttributeFilterUrlTuple {
  if (!Array.isArray(decoded) || decoded.length !== 3) {
    return false;
  }

  const [attributeDefinitionId, filterCondition, filterValue] = decoded;

  let filterValueValid = false;
  if (filterCondition === 'exists' || filterCondition === 'not_exists') {
    filterValueValid = filterValue === null;
  } else {
    filterValueValid =
      typeof filterValue === 'string' ||
      typeof filterValue === 'number' ||
      typeof filterValue === 'boolean';
  }

  return (
    typeof attributeDefinitionId === 'string' &&
    typeof filterCondition === 'string' &&
    filterValueValid
  );
}

function isAttributeFilterUrlTupleArray(
  decoded: unknown,
): decoded is AttributeFilterUrlTuple[] {
  return (
    Array.isArray(decoded) &&
    decoded.every((entry: unknown) => isAttributeFilterUrlTuple(entry))
  );
}

function assertValidAttributeCondition(
  attributeType: AttributeDefinitionMapValue['attributeDefinition']['type'],
  filterCondition: string,
): void {
  const condition = filterCondition.toUpperCase() as OperatorType;
  const validConditions = attributeTypeToOperatorTypeMap[attributeType];
  if (!validConditions?.includes(condition)) {
    throw new Error(
      `Invalid attribute filter condition "${filterCondition}" for type "${attributeType}".`,
    );
  }
}

function assertNumberValue(
  filterValue: AttributeFilterPrimitiveValue,
  attributeType: string,
): asserts filterValue is number {
  if (typeof filterValue !== 'number') {
    throw new TypeError(
      `Expected number value for attribute type "${attributeType}".`,
    );
  }
  if (Number.isNaN(filterValue) || !Number.isFinite(filterValue)) {
    throw new TypeError(
      `Invalid numeric value for attribute type "${attributeType}".`,
    );
  }
}

function assertBooleanValue(
  filterValue: AttributeFilterPrimitiveValue,
): asserts filterValue is boolean {
  if (typeof filterValue !== 'boolean') {
    throw new TypeError('Expected boolean value for attribute type "boolean".');
  }
}

function assertStringValue(
  filterValue: AttributeFilterPrimitiveValue,
  attributeType: string,
): asserts filterValue is string {
  if (typeof filterValue !== 'string') {
    throw new TypeError(
      `Expected string value for attribute type "${attributeType}".`,
    );
  }
}

function assertValidDateString(value: string): void {
  if (isEmpty(value)) {
    throw new Error('Date value cannot be empty.');
  }
  const attributeValue = new Date(value);
  if (Number.isNaN(attributeValue.getTime())) {
    throw new TypeError(`Invalid date value "${value}".`);
  }
}

function assertReferenceUuidValue(
  filterValue: AttributeFilterPrimitiveValue,
): asserts filterValue is UUID {
  if (typeof filterValue !== 'string' || !isUUID(filterValue)) {
    throw new TypeError('Expected UUID value for attribute type "reference".');
  }
}

function isRelationFilterUrlTuple(
  decoded: unknown,
): decoded is RelationFilterUrlTuple {
  if (!Array.isArray(decoded) || decoded.length !== 5) {
    return false;
  }
  const [condition, roleName, target, targetId, targetType] = decoded;
  return (
    typeof condition === 'string' &&
    isStringOrNull(roleName) &&
    typeof target === 'string' &&
    isStringArrayOrNull(targetId) &&
    isStringArrayOrNull(targetType)
  );
}

function isRelationFilterUrlTupleArray(
  decoded: unknown,
): decoded is RelationFilterUrlTuple[] {
  return (
    Array.isArray(decoded) &&
    decoded.every((entry: unknown) => isRelationFilterUrlTuple(entry))
  );
}

function isString(decoded: unknown): decoded is string {
  return typeof decoded === 'string';
}

function isPositiveInteger(decoded: unknown): decoded is number {
  return (
    typeof decoded === 'number' &&
    Number.isFinite(decoded) &&
    Number.isInteger(decoded) &&
    decoded > 0
  );
}

function safeDecodeParam<T>(
  value: string | undefined,
  isType: (decoded: unknown) => decoded is T,
): T {
  if (!value) {
    throw new Error('Missing query parameter value.');
  }

  if (value.length > MAX_RAW_LENGTH_PER_PARAM) {
    throw new Error('Query parameter exceeds maximum raw length.');
  }

  let decoded: unknown;
  try {
    decoded = JSON.parse(value);
  } catch {
    throw new Error(`Invalid JSON query parameter: "${value}".`);
  }

  if (!isType(decoded)) {
    throw new Error('Decoded query parameter does not match expected type.');
  }

  return decoded;
}

function buildAttributeDefinitionsMap(
  attributeCategoryDefinitions: AttributeCategoryDefinitionDto[],
): AttributeDefinitionsMap {
  const map: AttributeDefinitionsMap = {};
  for (const category of attributeCategoryDefinitions) {
    for (const attribute of category.attributeDefinitions) {
      map[attribute.id as UUID] = {
        categoryId: category.id as UUID,
        categoryName: category.name,
        categoryElementType: category.elementType,
        attributeDefinition: attribute,
      };
    }
  }
  return map;
}

function getAttributeDefinitionMapValue(
  attributeDefinitionsMap: AttributeDefinitionsMap,
  attributeDefinitionId: string,
): AttributeDefinitionMapValue | undefined {
  return attributeDefinitionsMap[attributeDefinitionId as UUID];
}

// Encoding
export function searchParametersToUrlQueryParameters(
  searchParams: SearchParameters,
  searchPageIndex?: number,
): Params {
  const searchTerms = searchParams.searchTerm;

  const scopeFilters = searchParams.advancedFilter?.scopeFilters ?? [];
  const typeFilters = searchParams.advancedFilter?.elementTypeFilters ?? [];
  const attributeFilters = searchParams.advancedFilter?.attributeFilters ?? [];
  const relationFilters = searchParams.advancedFilter?.relationFilters ?? [];
  const systemFilters = searchParams.advancedFilter?.systemAttributesFilter;

  const result: {
    searchTerm?: string;
    scopeFilters?: string;
    typeFilters?: string;
    attributeFilters?: string;
    relationFilters?: string;
    systemFilters?: string;
    pageIndex?: string;
    version: string;
  } = { version: '1.0.0' };

  if (searchTerms !== undefined && searchTerms !== null && searchTerms !== '') {
    result.searchTerm = JSON.stringify(searchTerms);
  }

  if (scopeFilters.length > 0) {
    result.scopeFilters = JSON.stringify(scopeFilters);
  }

  if (typeFilters.length > 0) {
    result.typeFilters = JSON.stringify(typeFilters);
  }

  if (attributeFilters.length > 0) {
    result.attributeFilters = JSON.stringify(
      attributeFilters.map((filter) => [
        filter.attribute.id,
        filter.condition,
        filter.attribute.value,
      ]),
    );
  }

  if (relationFilters.length > 0) {
    result.relationFilters = JSON.stringify(
      relationFilters.map((filter) => [
        filter.condition,
        filter.relation.roleName ?? '',
        filter.target,
        filter.target === 'specificObject'
          ? filter.relation?.relatedElement?.id
          : undefined,
        filter.target === 'specificObject' || filter.target === 'objectType'
          ? filter.relation?.relatedElement?.type
          : undefined,
      ]),
    );
  }

  if (systemFilters) {
    result.systemFilters = JSON.stringify(
      systemFilters.createdBy?.userId ?? '',
    );
  }

  if (searchPageIndex !== undefined) {
    result.pageIndex = JSON.stringify(searchPageIndex);
  }

  return result;
}

// Decoding scope filters
export function urlParamToScopeFilters(
  queryParameter: string | undefined,
): SearchScope[] {
  const decoded = safeDecodeParam<string[]>(queryParameter, isStringArray);
  const invalidScopes = decoded.filter(
    (scope) => !(defaultSearchScopes as readonly string[]).includes(scope),
  );
  if (invalidScopes.length > 0) {
    throw new Error(
      `Invalid scope filter value(s): ${invalidScopes.join(', ')}.`,
    );
  }
  return decoded as SearchScope[];
}

// Decoding element type filters
export function urlParamToElementTypeFilters(
  queryParameter: string | undefined,
): ObjectType[] {
  const decoded = safeDecodeParam<string[]>(queryParameter, isStringArray);
  const invalidTypes = decoded.filter(
    (type) => !(BDCore.objectTypes as readonly string[]).includes(type),
  );
  if (invalidTypes.length > 0) {
    throw new Error(
      `Invalid element type filter value(s): ${invalidTypes.join(', ')}.`,
    );
  }
  return decoded as ObjectType[];
}

// Decoding attribute filters
function parseAttributePayloadAndObjectId(
  attributeDefinition: AttributeDefinitionMapValue,
  filterCondition: string,
  filterValue: AttributeFilterPrimitiveValue,
): ParsedAttributePayloadAndObjectId {
  const attributeId = attributeDefinition.attributeDefinition.id;
  const attributeName = attributeDefinition.attributeDefinition.name;
  const attributeType = attributeDefinition.attributeDefinition.type;
  assertValidAttributeCondition(attributeType, filterCondition);
  const baseAttribute = {
    id: attributeId,
    name: attributeName,
    categoryId: attributeDefinition.categoryId,
    categoryName: attributeDefinition.categoryName,
  };
  let referencedObjectId: UUID | undefined = undefined;
  let attribute: AttributePayload<'BDCore'>;

  if (filterCondition === 'exists' || filterCondition === 'not_exists') {
    attribute = { ...baseAttribute, type: attributeType };
  } else {
    switch (attributeType) {
      case 'integer':
      case 'real':
      case 'money': {
        assertNumberValue(filterValue, attributeType);
        attribute = {
          ...baseAttribute,
          type: attributeType,
          value: filterValue,
        };
        break;
      }
      case 'boolean': {
        assertBooleanValue(filterValue);
        attribute = {
          ...baseAttribute,
          type: attributeType,
          value: filterValue,
        };
        break;
      }
      case 'date': {
        assertStringValue(filterValue, attributeType);
        assertValidDateString(filterValue);
        const attributeValue = new Date(filterValue);
        attribute = {
          ...baseAttribute,
          type: attributeType,
          value: attributeValue,
        };
        break;
      }
      case 'reference': {
        assertReferenceUuidValue(filterValue);
        referencedObjectId = filterValue;
        attribute = {
          ...baseAttribute,
          type: attributeType,
          value: filterValue,
        };
        break;
      }
      case 'string':
      case 'text':
      case 'enum': {
        assertStringValue(filterValue, attributeType);

        attribute = {
          ...baseAttribute,
          type: attributeType,
          value: filterValue,
        };
        break;
      }
      default:
        throw new Error(`Unsupported attribute type "${attributeType}".`);
    }
  }
  return { attribute, objectId: referencedObjectId };
}

function buildAttributeFilterValue(
  attributeDefinition: AttributeDefinitionMapValue,
  filterCondition: string,
  attribute: AttributePayload<'BDCore'>,
): AttributeFilterValue {
  return {
    ...defaultAttributeFilterValue,
    condition: filterCondition,
    attribute,
    isValid: true,
    elementType: attributeDefinition.categoryElementType,
    referencedObjectName: '',
    id: generateUUID(),
  };
}

function getAttributeFilterValueAndObjectId(
  attributeDefinitionId: string,
  filterCondition: string,
  filterValue: AttributeFilterPrimitiveValue,
  attributeDefinitionsMap: AttributeDefinitionsMap,
): AttributeFilterValueAndObjectId {
  const attributeDefinition: AttributeDefinitionMapValue | undefined =
    getAttributeDefinitionMapValue(
      attributeDefinitionsMap,
      attributeDefinitionId,
    );
  if (attributeDefinition === undefined) {
    throw new Error(
      `Unknown attribute definition id "${attributeDefinitionId}".`,
    );
  }
  const parsedFilterValueAndObjectId = parseAttributePayloadAndObjectId(
    attributeDefinition,
    filterCondition,
    filterValue,
  );
  if (parsedFilterValueAndObjectId.attribute === undefined) {
    throw new Error(
      `Unable to build attribute payload for "${attributeDefinitionId}".`,
    );
  }
  const attributeFilterValue = buildAttributeFilterValue(
    attributeDefinition,
    filterCondition,
    parsedFilterValueAndObjectId.attribute,
  );
  return {
    filterValue: attributeFilterValue,
    objectId: parsedFilterValueAndObjectId.objectId,
  };
}

function hasAttributeFilterValue(
  value: AttributeFilterValueAndObjectId | undefined,
): value is AttributeFilterValueAndObjectId & {
  filterValue: AttributeFilterValue;
} {
  return value?.filterValue !== undefined;
}

export function urlParamToAttributeFiltersAndObjectIds(
  queryParameter: string | undefined,
  attributeCategoryDefinitions: AttributeCategoryDefinitionDto[],
): AttributeFilterValueAndObjectId[] {
  const decoded = safeDecodeParam<AttributeFilterUrlTuple[]>(
    queryParameter,
    isAttributeFilterUrlTupleArray,
  );
  const attributeCategoryDefinitionsMap = buildAttributeDefinitionsMap(
    attributeCategoryDefinitions,
  );

  const sanitizedAttributeFilterValuesParameters = decoded
    .map((filter: AttributeFilterUrlTuple) => {
      const attributeDefinitionId = filter[0];
      const filterCondition = filter[1];
      const filterValue: AttributeFilterPrimitiveValue = filter[2];

      return getAttributeFilterValueAndObjectId(
        attributeDefinitionId,
        filterCondition,
        filterValue,
        attributeCategoryDefinitionsMap,
      );
    })
    .filter(hasAttributeFilterValue);

  return sanitizedAttributeFilterValuesParameters;
}

function resolveAttributeObjectName(
  filter: AttributeFilterValue,
  objectNames?: Map<UUID, string>,
): AttributeFilterValue {
  if (filter.attribute.type !== 'reference') {
    return filter;
  }
  if (!objectNames) {
    throw new Error('Missing object names map for reference attribute filter.');
  }
  const referenceId = filter.attribute.value;
  assertIsUUID(referenceId);
  const object = objectNames.get(referenceId);
  if (!object) {
    throw new Error(
      `Object name not found for reference id "${String(filter.attribute.value)}".`,
    );
  }
  return {
    ...filter,
    referencedObjectName: object,
  };
}

export function updateAttributeFiltersObjectsNames(
  attributeFilters: AttributeFilterValue[],
  objectNames?: Map<UUID, string>,
): AttributeFilterValue[] {
  return attributeFilters
    .map((filter) => resolveAttributeObjectName(filter, objectNames))
    .filter((f): f is AttributeFilterValue => !!f);
}

// Decoding relation filters
function isValidRelationFilterParams(
  filterCondition: string,
  filterRoleName: string,
  filterTarget: string,
  filterTargetId: string[],
  filterTargetType: string[],
): boolean {
  if (
    !(RelationConditions as readonly string[]).includes(filterCondition) ||
    !(RoleNames as readonly string[]).includes(filterRoleName) ||
    !(RelationTargets as readonly string[]).includes(filterTarget)
  ) {
    return false;
  }
  if (filterTarget === 'objectType' || filterTarget === 'specificObject') {
    if (!BDCoreMetaModelDefinition.isObjectType(filterTargetType?.[0])) {
      return false;
    }
  }
  if (filterTarget === 'specificObject') {
    if (!isUUIDs(filterTargetId)) {
      return false;
    }
  }
  return true;
}

function parseRelationFilterValueAndObjectId(
  filterCondition: string,
  filterRoleName: string,
  filterTarget: string,
  filterTargetId: string[],
  filterTargetType: string[],
): {
  relation: RoleCondition<'BDCore'>;
  target: RelationTarget;
  referencedObjectId?: UUID;
} {
  const relation: RoleCondition<'BDCore'> = { roleName: filterRoleName };
  const target = filterTarget as RelationTarget;
  let referencedObjectId: UUID | undefined = undefined;
  if (target === 'objectType' && filterTargetType) {
    relation.relatedElement = { type: filterTargetType };
  } else if (
    target === 'specificObject' &&
    filterTargetId &&
    filterTargetType
  ) {
    const targetId = filterTargetId[0];
    assertIsUUID(targetId);
    referencedObjectId = targetId;
    relation.relatedElement = {
      id: filterTargetId.map((id) => {
        assertIsUUID(id);
        return id;
      }),
      type: filterTargetType,
      name: [''],
    };
  } else {
    relation.relatedElement = {};
  }
  return { relation, target, referencedObjectId };
}

function buildRelationFilterValueAndObjectId(
  filterCondition: string,
  relation: RoleCondition<'BDCore'>,
  target: RelationTarget,
  referencedObjectId?: UUID,
): RelationFilterValueAndObjectId {
  const relationFilterValue: RelationFilterValue = {
    id: generateUUID(),
    relation,
    condition: filterCondition as RelationCondition,
    isValid: true,
    target,
  };
  return { filterValue: relationFilterValue, objectId: referencedObjectId };
}

function getRelationFilterValueAndObjectId(
  filterCondition: string,
  filterRoleName: string,
  filterTarget: string,
  filterTargetId: string[],
  filterTargetType: string[],
): RelationFilterValueAndObjectId {
  if (
    !isValidRelationFilterParams(
      filterCondition,
      filterRoleName,
      filterTarget,
      filterTargetId,
      filterTargetType,
    )
  ) {
    throw new Error(
      `Invalid relation filter parameters: condition="${filterCondition}", roleName="${filterRoleName}", target="${filterTarget}".`,
    );
  }
  const { relation, target, referencedObjectId } =
    parseRelationFilterValueAndObjectId(
      filterCondition,
      filterRoleName,
      filterTarget,
      filterTargetId,
      filterTargetType,
    );
  return buildRelationFilterValueAndObjectId(
    filterCondition,
    relation,
    target,
    referencedObjectId,
  );
}

function hasRelationFilterValue(
  value: RelationFilterValueAndObjectId | undefined,
): value is RelationFilterValueAndObjectId & {
  filterValue: RelationFilterValue;
} {
  return value?.filterValue !== undefined;
}

export function urlParamToRelationFiltersAndObjectIds(
  queryParameter: string | undefined,
): RelationFilterValueAndObjectId[] {
  const decoded = safeDecodeParam<RelationFilterUrlTuple[]>(
    queryParameter,
    isRelationFilterUrlTupleArray,
  );
  return decoded
    .map((filter: RelationFilterUrlTuple) => {
      const filterCondition = filter[0];
      const filterRoleName = filter[1] ?? '';
      const filterTarget = filter[2];
      const filterTargetId = filter[3];
      const filterTargetType = filter[4];
      if (filterTarget === 'specificObject') {
        if (
          !Array.isArray(filterTargetId) ||
          !Array.isArray(filterTargetType)
        ) {
          throw new TypeError(
            'Invalid specificObject relation filter: targetId and targetType must be arrays.',
          );
        }
        if (filterTargetId.length !== 1 || filterTargetType?.length !== 1) {
          throw new Error(
            'Invalid specificObject relation filter: expected exactly one targetId and one targetType.',
          );
        }
      } else if (filterTarget === 'objectType') {
        if (!Array.isArray(filterTargetType)) {
          throw new TypeError(
            'Invalid objectType relation filter: targetType must be an array.',
          );
        }
        if (filterTargetType.length !== 1) {
          throw new Error(
            'Invalid objectType relation filter: expected exactly one targetType.',
          );
        }
        if (filterTargetId !== null) {
          throw new Error(
            'Invalid objectType relation filter: targetId must be null.',
          );
        }
      } else if (filterTargetId !== null || filterTargetType !== null) {
        throw new Error(
          `Invalid relation filter: target "${filterTarget}" must not include targetId/targetType.`,
        );
      }
      return getRelationFilterValueAndObjectId(
        filterCondition,
        filterRoleName,
        filterTarget,
        filterTargetId ?? [],
        filterTargetType ?? [],
      );
    })
    .filter(hasRelationFilterValue);
}

function resolveRelationObjectName(
  filter: RelationFilterValue,
  objectNames?: Map<UUID, string>,
): RelationFilterValue {
  if (filter.target !== 'specificObject') {
    return filter;
  }
  if (!objectNames) {
    throw new Error(
      'Object names map is required for specificObject relation filters.',
    );
  }
  if (!filter.relation.relatedElement?.id) {
    throw new Error(
      'Missing related element id for specificObject relation filter.',
    );
  }
  const relatedElementId = filter.relation.relatedElement.id[0];
  assertIsUUID(relatedElementId);
  const referencedObject = objectNames.get(relatedElementId);
  if (!referencedObject) {
    throw new Error(
      `Referenced object name not found for id "${filter.relation.relatedElement.id[0]}".`,
    );
  }
  return {
    ...filter,
    relation: {
      ...filter.relation,
      relatedElement: {
        ...filter.relation.relatedElement,
        name: [referencedObject],
      },
    },
  };
}

export function updateRelationFiltersObjectsNames(
  relationFilters: RelationFilterValue[],
  objectNames?: Map<UUID, string>,
): RelationFilterValue[] {
  return relationFilters
    .map((filter) => resolveRelationObjectName(filter, objectNames))
    .filter((f): f is RelationFilterValue => !!f);
}

// Decoding system attribute filters
export function urlParamToSystemAttributesFilter(
  queryParameter: string | undefined,
): SystemAttributesFilter | undefined {
  const decoded = safeDecodeParam<string>(queryParameter, isString);

  if (!isUUID(decoded)) {
    throw new Error(
      `Invalid system attribute filter: expected UUID string, received "${decoded}".`,
    );
  }

  return {
    createdBy: {
      userId: decoded,
      firstName: '',
      lastName: '',
      email: '',
    },
  };
}

export function updateSystemAttributeFilterUserDetails(
  systemAttributesFilter: SystemAttributesFilter | undefined,
  users?: SystemAttributesUser[],
): SystemAttributesFilter {
  if (!systemAttributesFilter?.createdBy) {
    throw new Error('Invalid system attributes filter: createdBy is required.');
  }
  if (!users || users.length === 0) {
    throw new Error(
      `No users provided for system attribute filter user "${systemAttributesFilter.createdBy.userId}".`,
    );
  }
  const usersMap: Map<UUID, SystemAttributesUser> = new Map();
  users.forEach((user) => {
    usersMap.set(user.userId, user);
  });
  const createdByUserId = systemAttributesFilter.createdBy.userId;
  assertIsUUID(createdByUserId);
  const user = usersMap.get(createdByUserId);
  if (!user) {
    throw new Error(
      `User "${systemAttributesFilter.createdBy.userId}" not found in system attributes users.`,
    );
  }
  return {
    ...systemAttributesFilter,
    createdBy: user,
  };
}

export function urlParamToObjectId(queryParameter: string | undefined): UUID {
  const decoded = safeDecodeParam<string>(queryParameter, isString);
  if (!isUUID(decoded)) {
    throw new Error(`Invalid object id query parameter: "${decoded}".`);
  }
  return decoded;
}

// Decoding search term
export function urlParamToSearchTerm(
  queryParameter: string | undefined,
): string {
  return safeDecodeParam<string>(queryParameter, isString);
}

// Decoding search page index
export function urlParamToSearchPageIndex(
  queryParameter: string | undefined,
): number {
  return safeDecodeParam<number>(queryParameter, isPositiveInteger);
}

// Encoding cypher search params (saved cypher search id + page).
export function cypherSearchToUrlQueryParameters(
  cypherSearchId: UUID,
  searchPageIndex?: number,
): Params {
  const result: {
    cypherSearchId: string;
    pageIndex?: string;
    version: string;
  } = {
    cypherSearchId: JSON.stringify(cypherSearchId),
    version: '1.0.0',
  };
  if (searchPageIndex !== undefined) {
    result.pageIndex = JSON.stringify(searchPageIndex);
  }
  return result;
}

// Decoding cypher search id
export function urlParamToCypherSearchId(
  queryParameter: string | undefined,
): UUID {
  const decoded = safeDecodeParam<string>(queryParameter, isString);
  assertIsUUID(decoded);
  return decoded;
}

// Comparison
function omitIdFromFilters<T extends { id: unknown }>(
  arr: T[] = [],
): Omit<T, 'id'>[] {
  return arr.map(({ id, ...rest }) => rest);
}

export function areScopeFiltersEqual(
  a: SearchScope[],
  b: SearchScope[],
): boolean {
  return isEqual(a, b);
}

export function areTypeFiltersEqual(a: ObjectType[], b: ObjectType[]): boolean {
  return isEqual(a, b);
}

export function areAttributeFiltersEqual(
  a: AttributeFilterValue[],
  b: AttributeFilterValue[],
): boolean {
  return isEqual(omitIdFromFilters(a), omitIdFromFilters(b));
}

export function areRelationFiltersEqual(
  a: RelationFilterValue[],
  b: RelationFilterValue[],
): boolean {
  return isEqual(
    omitIdFromFilters<RelationFilterValue>(a),
    omitIdFromFilters<RelationFilterValue>(b),
  );
}

export function areSystemFiltersEqual(
  a: SystemAttributesFilter | undefined,
  b: SystemAttributesFilter | undefined,
): boolean {
  return isEqual(a, b);
}

export function areSearchTermsEqual(a: string, b: string): boolean {
  return isEqual(a, b);
}

export function areSearchPageIndexesEqual(a: number, b: number): boolean {
  return isEqual(a, b);
}
